/**
 * #91 — the ONE predicate for "the database this picker is holding is not one
 * it can offer", plus the sentence that explains why.
 *
 * A shadcn/Radix `<Select>` whose `value` matches no rendered `<SelectItem>`
 * paints neither the value nor the placeholder — the trigger is simply empty
 * (measured; it is also the mechanism B90/#90 fixed for a different cause).
 * So a picker holding a database the server did not list renders as a blank
 * field with no explanation, and nothing downstream disagrees: Schema Compare's
 * `Open Compare` stays enabled.
 *
 * That is reachable without anything being broken. "Recently compared" entries
 * are persisted (20 of them, across restarts) and restore a `(connection,
 * database)` pair recorded at some point in the past. Between then and now the
 * database can have been dropped or renamed, the login can have lost the
 * permission that made it visible, or the user can have switched the
 * connection's own "Limit connection to selected database" flag on — which
 * narrows the listing to exactly one name (`applyDatabaseLimit`).
 *
 * The three causes are NOT equally knowable, which is why this returns a
 * reason rather than a boolean: the limit is Jam's own setting and can be read
 * back with certainty, so that case is stated as fact. Everything else is
 * genuinely ambiguous from the renderer, so its sentence names the
 * possibilities instead of picking one.
 *
 * Deliberately silent in three states, because in each of them "not in the
 * list" is not yet a fact about the database:
 *   - the listing is still in flight;
 *   - the listing did not return `'ok'` — `'error'` / `'empty'` are the C2
 *     fallback surface's business (`DatabaseListingFallbackBanner` already
 *     says the list is not authoritative, and inventing a second, more
 *     specific claim on top of an unauthoritative list would be a guess);
 *   - no database is selected at all.
 */

import {
  findDatabaseInList,
  getLimitedDatabase,
  type ConnectionDatabaseLimit,
} from '../../connections/utils/connectionDatabaseLimit';
import type { DatabasesListingState } from '../../database-objects/services/databasesService';

/**
 * `'limited'` — Jam itself is withholding the rest of the server, because this
 * connection carries "Limit connection to selected database". Certain.
 * `'absent'` — the server listed its databases and this one was not among
 * them. The cause is not knowable from here.
 */
export type UnlistedDatabaseReason = 'limited' | 'absent';

export type UnlistedDatabaseInput = {
  /** The database the picker currently holds. */
  database: string | undefined;
  /** The listing the picker can offer. Authoritative only when `state === 'ok'`. */
  databases: readonly string[] | undefined;
  /** `getDatabases()`' result state. */
  state: DatabasesListingState | undefined;
  /** True while the listing is in flight. */
  isLoading?: boolean;
  /** The saved/active connection, for the "Limit connection to…" read-back. */
  connection?: ConnectionDatabaseLimit;
};

/**
 * Why the selected database is missing from the listing, or `undefined` when
 * it is not missing (or when we are in no position to say).
 *
 * Presence is decided by {@link findDatabaseInList}, i.e. case-insensitively
 * with an exact match preferred — the same rule the rest of the app uses to
 * decide whether a name is "the same database". A recent whose casing drifted
 * from the server's is therefore NOT reported as missing, which is correct:
 * the database is there and the comparison will run. (Its trigger can still
 * paint blank, because Radix matches `value` byte-for-byte. Rendering a second
 * row for a database already in the list would be worse than the blank, and
 * the case can only arise if the server's own casing changed after the entry
 * was recorded.)
 */
export function unlistedDatabaseReason({
  database,
  databases,
  state,
  isLoading,
  connection,
}: UnlistedDatabaseInput): UnlistedDatabaseReason | undefined {
  const wanted = database?.trim();
  if (!wanted) return undefined;
  if (isLoading) return undefined;
  if (state !== 'ok') return undefined;
  if (findDatabaseInList(databases, wanted) !== undefined) return undefined;

  const limit = getLimitedDatabase(connection);
  return limit !== undefined ? 'limited' : 'absent';
}

/**
 * The sentence shown to the user. Lives here, next to the predicate, so the
 * two cannot drift — the same reason for centralising
 * `fallbackTooltip.ts`'s copy.
 *
 * Neither branch claims the database does or does not exist: the `'limited'`
 * one states what Jam is doing, the `'absent'` one states what the server
 * answered and lists the possibilities.
 */
export function unlistedDatabaseMessage(
  reason: UnlistedDatabaseReason,
  database: string,
  connection?: ConnectionDatabaseLimit,
): string {
  if (reason === 'limited') {
    const limit = getLimitedDatabase(connection);
    return `This connection is limited to ${limit}, so ${database} isn't offered here. Turn off "Limit connection to selected database" in the connection's settings to use it.`;
  }
  return `${database} isn't in this server's database list — it may have been dropped or renamed, or this login may not be able to see it.`;
}
